import {
	AssemblyOperationResponse,
	AssemblyTechMapResponse,
	PreformOperationResponse,
	PreformTechMapResponse,
	ProductOperationDomain,
	ProductOperationUpdate,
	ProductTechMapResponse,
	RubberOperationResponse,
	RubberTechMapResponse,
} from '@/shared/state/api/swagger';

interface HasStepId {
	step_id?: number | string;
}

interface HasName {
	name?: string;
}

interface HasId {
	id?: number | string;
}

type Operation = HasStepId & HasName;
type OperationWithId = Operation & HasId;

export type TechMap = ProductTechMapResponse | PreformTechMapResponse | AssemblyTechMapResponse | RubberTechMapResponse;
export type OperationResponse =
	| ProductOperationDomain
	| PreformOperationResponse
	| AssemblyOperationResponse
	| ProductOperationUpdate
	| RubberOperationResponse;

const isProductTechMap = (map: unknown): map is ProductTechMapResponse =>
	typeof map === 'object' && map !== null && 'product_operations' in map;
const isPreformTechMap = (map: unknown): map is PreformTechMapResponse =>
	typeof map === 'object' && map !== null && 'preform_operations' in map;
const isAssemblyTechMap = (map: unknown): map is AssemblyTechMapResponse =>
	typeof map === 'object' && map !== null && 'assembly_operations' in map;
const isRubberTechMap = (map: unknown): map is RubberTechMapResponse =>
	typeof map === 'object' && map !== null && 'rubber_operations' in map;

const sortByStepId = (a: HasStepId, b: HasStepId): number => {
	return Number(a.step_id) - Number(b.step_id);
};

export const sortByOperations = <T extends HasStepId>(operations: T[] = []): T[] => {
	return [...operations].sort(sortByStepId);
};

export function sortOperations(techMap: ProductTechMapResponse): ProductOperationDomain[];
export function sortOperations(techMap: PreformTechMapResponse): PreformOperationResponse[];
export function sortOperations(techMap: AssemblyTechMapResponse): AssemblyOperationResponse[];
export function sortOperations(techMap: RubberTechMapResponse): RubberOperationResponse[];
export function sortOperations(operationsPayload: ProductOperationUpdate[]): ProductOperationUpdate[];
export function sortOperations(input: TechMap | ProductOperationUpdate[]): OperationResponse[] {
	if (Array.isArray(input)) {
		return sortByOperations(input);
	}
	if (isProductTechMap(input)) {
		return sortByOperations([...(input.product_operations ?? []), ...(input.default_product_operations ?? [])]);
	}
	if (isPreformTechMap(input)) {
		return sortByOperations(input.preform_operations ?? []);
	}
	if (isAssemblyTechMap(input)) {
		return sortByOperations(input.assembly_operations ?? []);
	}
	if (isRubberTechMap(input)) {
		return sortByOperations(input.rubber_operations ?? []);
	}
	return [];
}

export const findOperationByStepId = <T extends HasStepId>(operations: T[], stepId: number): T | undefined => {
	return operations.find((op) => Number(op.step_id) === stepId);
};

export const findOperationByStepIdAndIdx = <T extends HasStepId & HasName>(
	operations: T[],
	stepId: number,
	idx: number,
): T | undefined => {
	return operations.find((op, i) => Number(op.step_id) === stepId && i === idx);
};

export const findOperationById = <T extends HasId>(operations: T[], id: number | string): T | undefined => {
	return operations.find((op) => op.id === id || Number(op.id) === Number(id));
};

function getTypeSafeOperations(techMap: TechMap): OperationResponse[] {
	if (isProductTechMap(techMap)) {
		return sortOperations(techMap);
	}
	if (isPreformTechMap(techMap)) {
		return sortOperations(techMap);
	}
	if (isAssemblyTechMap(techMap)) {
		return sortOperations(techMap);
	}
	if (isRubberTechMap(techMap)) {
		return sortOperations(techMap);
	}
	return [];
}

export function getOperationByStepId(
	techMap: ProductTechMapResponse,
	stepId: number,
): ProductOperationDomain | undefined;
export function getOperationByStepId(
	techMap: PreformTechMapResponse,
	stepId: number,
): PreformOperationResponse | undefined;
export function getOperationByStepId(
	techMap: AssemblyTechMapResponse,
	stepId: number,
): AssemblyOperationResponse | undefined;
export function getOperationByStepId(
	techMap: RubberTechMapResponse,
	stepId: number,
): RubberOperationResponse | undefined;
export function getOperationByStepId(techMap: TechMap, stepId: number): OperationResponse | undefined {
	const operations = getTypeSafeOperations(techMap);
	return findOperationByStepId(operations, stepId);
}

export function getOperationByStepIdAndIdx(
	techMap: AssemblyTechMapResponse,
	stepId: number,
	idx: number,
): AssemblyOperationResponse | undefined {
	const operations = getTypeSafeOperations(techMap) as AssemblyOperationResponse[];
	return findOperationByStepIdAndIdx(operations, stepId, idx);
}

export function getOperationById(
	techMap: ProductTechMapResponse,
	id: number | string,
): ProductOperationDomain | undefined;
export function getOperationById(
	techMap: PreformTechMapResponse,
	id: number | string,
): PreformOperationResponse | undefined;
export function getOperationById(
	techMap: AssemblyTechMapResponse,
	id: number | string,
): AssemblyOperationResponse | undefined;
export function getOperationById(
	techMap: RubberTechMapResponse,
	id: number | string,
): RubberOperationResponse | undefined;
export function getOperationById(techMap: TechMap, id: number | string): OperationResponse | undefined {
	const operations = getTypeSafeOperations(techMap);
	return findOperationById(operations, id);
}

export const sortAllOperations = (techMap: ProductTechMapResponse): ProductOperationDomain[] => {
	return sortOperations(techMap);
};

type MappedOperation = { label: string; value: number | string };

const createStepIdLabel = (operation: Operation): string => `${operation.step_id} - ${operation.name ?? ''}`;

const createNameLabel = (operation: Operation): string => operation.name ?? '';

const mapTechMapOperations = (techMap: TechMap, labelFn: (operation: Operation) => string): MappedOperation[] => {
	const operations = getTypeSafeOperations(techMap);
	return operations.map((op) => ({
		label: labelFn(op),
		value: isAssemblyTechMap(techMap) ? op.id ?? '' : op.step_id ?? '',
	}));
};

export function mapOperationsByStepId(techMap: TechMap): MappedOperation[] {
	return mapTechMapOperations(techMap, createStepIdLabel);
}

export function mapOperations(techMap: TechMap): MappedOperation[] {
	return mapTechMapOperations(techMap, createNameLabel);
}

export function mapOperationsById(techMap: TechMap): MappedOperation[] {
	const operations = getTypeSafeOperations(techMap);
	return operations.map((op) => ({
		label: createStepIdLabel(op),
		value: op.id ?? '',
	}));
}
